import React, { useEffect, useMemo, useState } from 'react';
import { Box, Divider, Stack, SwipeableDrawer, Typography } from '@mui/material';
import { ActivityLog } from '@api/ActivityLogs/ActivityLog';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { setActivityDrawer } from '@store/appStore';
import ActivityLogsService from '@api/ActivityLogs/ActivityLogsCtrl';
import { showAlertError } from '@utils/Alerts/Error';
import { toDateSafe } from '@utils/Date';

const FECHA = 'dd-MM-yyyy p';

const ActivityDrawer: React.FunctionComponent = () => {
    const { activityLogDrawer } = useSelector((state: RootReducer) => state.app);
    const activityLogsService = useMemo(() => new ActivityLogsService(), []);
    const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
    const dispatch = useDispatch();

    // Los renglones que la pantalla DERIVÓ del objeto (D8). No son documentos de
    // `activity-logs`: no se leyó ni se escribió nada para tenerlos.
    const derived = activityLogDrawer?.derived;

    /**
     * Los eventos registrados y los derivados, **intercalados por fecha**. Un
     * derivado no siempre es el más viejo: "lo entregó Uber" puede ser posterior
     * a la última acción que alguien hizo desde el admin.
     */
    const entries = useMemo(() => {
        const registrados = activityLogs.map((log, index) => ({
            key: log.id || `log-${index}`,
            millis: toDateSafe(log.date)?.getTime() ?? 0,
            text: `${log.name} ${log.description}`,
            isDerived: false
        }));
        const derivados = (derived || []).map((entry, index) => ({
            key: `derived-${index}`,
            millis: entry.dateMillis,
            text: entry.text,
            isDerived: true
        }));
        return [...registrados, ...derivados].sort((a, b) => b.millis - a.millis);
    }, [activityLogs, derived]);

    const onOpen = () => {
        dispatch(
            setActivityDrawer({
                isOpen: true
            })
        );
    };

    const onClose = () => {
        dispatch(
            setActivityDrawer({
                isOpen: false
            })
        );
    };

    const getData = async () => {
        try {
            if (activityLogDrawer?.section && activityLogDrawer?.sectionId) {
                const activityLogsData = await activityLogsService.getActivityLogs(
                    activityLogDrawer?.section,
                    activityLogDrawer?.sectionId
                );
                setActivityLogs(activityLogsData);
            }
        } catch (error) {
            console.log(error);
            showAlertError(error);
        }
    };

    useEffect(() => {
        if (activityLogDrawer?.isOpen) {
            setActivityLogs([]);
            void getData();
        }
    }, [activityLogDrawer?.isOpen, activityLogDrawer?.sectionId]);



    return (
        <SwipeableDrawer
            anchor="right"
            open={activityLogDrawer?.isOpen}
            onClose={onClose}
            onOpen={onOpen}
            style={{ zIndex: 9999 }}
        >
            <Box p={4} width={350}>
                <Typography variant="h6" fontWeight={300} mb={2}>
                    Bitácora
                </Typography>

                {entries.length > 0 ? (
                    <Stack spacing={2}>
                        {entries.map((entry, index) => (
                            <Stack key={entry.key} position="relative">
                                <Stack spacing={1}>
                                    {/*
                                        Los derivados van en gris y cursiva: es lo
                                        que el objeto sabe de sí mismo, no un
                                        evento que alguien registró.
                                    */}
                                    <Typography
                                        variant="subtitle1"
                                        color={entry.isDerived ? 'text.secondary' : undefined}
                                        fontStyle={entry.isDerived ? 'italic' : undefined}
                                    >
                                        {entry.text}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {entry.millis ? format(new Date(entry.millis), FECHA) : 'sin fecha'}
                                    </Typography>
                                </Stack>
                                {index < entries.length - 1 && <Divider sx={{ mt: 2 }} />}
                            </Stack>
                        ))}
                    </Stack>
                ) : (
                    <Typography variant="body1" color="text.secondary">
                        Sin actividad registrada
                    </Typography>
                )}
            </Box>
        </SwipeableDrawer>
    );
};

export default ActivityDrawer;
